<template>
  <v-tooltip bottom z-index="999" :open-on-hover="!isMobile" v-model="showTooltip">
    <template v-slot:activator="{ on, attrs }">
      <div
        v-if="isRttCompatible || isPerformanceCompatible"
        class="d-flex"
        v-on="on"
        v-bind="attrs"
        @click="wasClicked = true"
      >
        <span class="d-flex flex-row" v-if="!isOnline">
          <v-icon color="red">mdi-signal-cellular-outline</v-icon>
          <v-icon color="red">error</v-icon>
        </span>
        <span class="d-flex flex-row" v-else-if="connectionScore == 1">
          <v-icon color="yellow">mdi-signal-cellular-1</v-icon>
          <v-icon color="yellow">report_problem</v-icon>
        </span>
        <v-icon v-else-if="connectionScore == 2">mdi-signal-cellular-2</v-icon>
        <v-icon v-else-if="connectionScore == 3">mdi-signal-cellular-3</v-icon>
      </div>
    </template>
    <span>{{ connectivityTip }}</span>
  </v-tooltip>
</template>

<script>
import { createComponent, onBeforeDestroy, onCreated, value, computed, watch } from "vue-function-api";
import { i18n } from "../plugins/i18n";
import {
  checkIsRttCompatible,
  checkIsPerformanceCompatible,
  getLatencyByNetInfo,
  getLatencyByPerformance,
} from "@/framework/helper/connectivity";
import { ProjectService } from "@/framework/services/general/projectService";

export default createComponent({
  setup(props, context) {
    const isRttCompatible = value(false);
    const isPerformanceCompatible = value(false);
    const isOnline = value(false);
    const connectionScore = value(3);
    const currentLatency = value(0);
    const showTooltip = value(false);
    const wasClicked = value(false);
    const isMobile = value(false);

    // confirm the case for starting the auto disable (the tooltip is 1. opened, 2. via click)
    watch(
      () => showTooltip.value,
      (newVal, oldVal) => {
        if (newVal == true && oldVal == false && wasClicked.value) disableTipAfterDelay();
      }
    );

    // change the tip to be displayed on hover/click/etc based on connectivity score
    const connectivityTip = computed(() => {
      var newTip = "";
      var latencyreport = " (Latency: " + currentLatency.value.toFixed(0) + "ms)";
      if (!isOnline.value) {
        newTip = `${i18n.t("connectivity_lost")}`.toString();
      } else if (connectionScore.value > 1) {
        newTip = `${i18n.t("connectivity_good")}`.toString() + latencyreport;
      } else {
        newTip = `${i18n.t("connectivity_poor")}`.toString() + latencyreport;
      }
      return newTip;
    });

    // setup an interval function for updating latency score on an interval (hard-coded as 20sec, currently)
    let recheckLatencyObject;

    const recheckLatency = () => {
      recheckLatencyObject = setInterval(
        function () {
          if (!isOnline.value) return;
          connectionScore.value = scoreLatency(10);
        },
        20000 //time in milliseconds
      );
    };

    // enforce disabling of the tooltip after some delay (default 3sec)
    const disableTipAfterDelay = () => {
      setTimeout(() => (showTooltip.value = false), 2000);
    };

    onCreated(() => {
      let networkInfo = window.navigator.connection;
      isRttCompatible.value = checkIsRttCompatible(networkInfo);
      isPerformanceCompatible.value = checkIsPerformanceCompatible();
      if (!isRttCompatible && !isPerformanceCompatible) {
        console.error("Error: no method for checking connectivity is available, disabling indicator");
      }

      isOnline.value = window.navigator.onLine; // !!this does not ensure connectivity! only 100% true is "false=offline"!!

      var projectService = new ProjectService();
      isMobile.value = projectService.getIsMobile();

      connectionScore.value = scoreLatency();
      window.addEventListener("online", function (e) {
        isOnline.value = true;
      });
      window.addEventListener("offline", function (e) {
        isOnline.value = false;
      });
      if (isRttCompatible.value) {
        // navigator.connection is experimental, and doesn't work in safari (and IE, but not relevant)
        networkInfo.addEventListener("change", function (e) {
          connectionScore.value = scoreLatency();
        });
      } else {
        recheckLatency();
        window.addEventListener("online", function (e) {
          connectionScore.value = scoreLatency(10);
        });
      }
    });

    // return a relative score between 1 and 3 "bars", based on acceptable latency standards for apps
    const scoreLatency = (sampleSizeToTest) => {
      let rttLatencyAmount = 0;
      let perfLatencyAmount;

      // get a latency measure via any available methods
      if (isRttCompatible.value) {
        rttLatencyAmount = getLatencyByNetInfo(window.navigator.connection);
      }
      perfLatencyAmount = getLatencyByPerformance(sampleSizeToTest);

      // average the measures to try to get a more representative measure of latency
      currentLatency.value =
        isRttCompatible.value && rttLatencyAmount != 0 ? (rttLatencyAmount + perfLatencyAmount) / 2 : perfLatencyAmount;

      if (currentLatency.value < 200) {
        return 3;
      } else if (currentLatency.value < 400) {
        //400ms is generally the max "accpetable" latency for a web app, but still noticeably slow
        return 2;
      } else {
        // above 400ms causes apps to seem unresponsive and borderline frustrating
        return 1;
      }
    };

    // intervals must be purposefully cleaned up, otherwise they'll run forever, as long as the app is open
    onBeforeDestroy(() => {
      clearInterval(recheckLatencyObject);
    });

    return {
      isRttCompatible,
      isPerformanceCompatible,
      isOnline,
      isMobile,
      connectionScore,
      recheckLatency,
      connectivityTip,
      showTooltip,
      wasClicked,
    };
  },
});
</script>
<style scoped></style>
